/**
 * Terms of Service Page — Legacy Triangle
 */

import React, { useEffect } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { FileText, Mail, Phone } from "lucide-react";

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0f2d52]">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0f2d52] via-[#1a3a5c] to-[#0f2d52]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-[#48d480]/10 border border-[#48d480]/30 rounded-full px-4 py-2 mb-6">
            <FileText className="w-4 h-4 text-[#48d480]" />
            <span className="text-[#48d480] text-sm font-medium">Legal Agreement</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Terms of Service
          </h1>
          <p className="text-white/70 text-lg">Last updated: March 18, 2026</p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none" style={{ fontFamily: "'Inter', sans-serif" }}>
            
            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-600 leading-relaxed">
                By accessing or using the Legacy Triangle website and services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using or accessing this site. The materials contained in this website are protected by applicable copyright and trademark law.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">2. Description of Services</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                Legacy Triangle provides business funding consultation and brokerage services, including but not limited to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Business credit building guidance and education</li>
                <li>Funding application assistance and processing</li>
                <li>Connection to lending partners and financial institutions</li>
                <li>Business financial consulting services</li>
                <li>Investment opportunities in real estate and business acquisitions</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">3. User Responsibilities</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                As a user of our services, you agree to:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain the confidentiality of any account credentials</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Use our services only for lawful purposes</li>
                <li>Not misrepresent your identity or business information</li>
                <li>Not attempt to interfere with the proper functioning of our website</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">4. No Guarantee of Funding</h2>
              <p className="text-gray-600 leading-relaxed">
                Legacy Triangle acts as a funding consultant and broker. We do not guarantee approval of any funding application. Funding decisions are made solely by our lending partners based on their own criteria and underwriting standards. We make no representations or warranties regarding the likelihood of approval, funding amounts, or terms offered by lenders.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">5. Fees and Payments</h2>
              <p className="text-gray-600 leading-relaxed">
                Certain services may require payment of fees. All fees will be disclosed prior to the provision of services. Payment terms and conditions will be specified in separate agreements as applicable. We reserve the right to modify our fee structure with reasonable notice to clients.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">6. Intellectual Property</h2>
              <p className="text-gray-600 leading-relaxed">
                The content, features, and functionality of this website, including but not limited to text, graphics, logos, icons, images, audio clips, and software, are owned by Legacy Triangle or its licensors and are protected by United States and international copyright, trademark, and other intellectual property laws. You may not reproduce, distribute, modify, or create derivative works without our express written permission.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">7. Limitation of Liability</h2>
              <p className="text-gray-600 leading-relaxed">
                To the fullest extent permitted by law, Legacy Triangle shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from your use of our services or website.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">8. Disclaimer of Warranties</h2>
              <p className="text-gray-600 leading-relaxed">
                Our services and website are provided "as is" and "as available" without any warranties of any kind, either express or implied. We do not warrant that our services will be uninterrupted, error-free, or completely secure. We disclaim all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">9. Indemnification</h2>
              <p className="text-gray-600 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Legacy Triangle, its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorney's fees, arising out of or in any way connected with your access to or use of our services or your violation of these Terms.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">10. Third-Party Links</h2>
              <p className="text-gray-600 leading-relaxed">
                Our website may contain links to third-party websites or services that are not owned or controlled by Legacy Triangle. We have no control over, and assume no responsibility for, the content, privacy policies, or practices of any third-party websites or services. You acknowledge and agree that we shall not be responsible or liable for any damage or loss caused by the use of any such content, goods, or services.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">11. Governing Law</h2>
              <p className="text-gray-600 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of North Carolina, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts located in Wake County, North Carolina.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">12. Changes to Terms</h2>
              <p className="text-gray-600 leading-relaxed">
                We reserve the right to modify or replace these Terms at any time at our sole discretion. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion. By continuing to access or use our services after those revisions become effective, you agree to be bound by the revised terms.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">13. Severability</h2>
              <p className="text-gray-600 leading-relaxed">
                If any provision of these Terms is held to be unenforceable or invalid, such provision will be changed and interpreted to accomplish the objectives of such provision to the greatest extent possible under applicable law, and the remaining provisions will continue in full force and effect.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">14. SMS Terms</h2>
              <p className="text-gray-600 leading-relaxed">
                By opting in to receive SMS messages from Legacy Triangle Inc., you consent to receive service-related text messages including funding updates, application notifications, and appointment reminders. Consent to receive SMS is not a condition of purchase. Message frequency may vary. Message and data rates may apply. Reply STOP to unsubscribe and HELP for assistance.
              </p>
            </div>

            <div className="bg-[#0f2d52]/5 rounded-xl p-6">
              <h2 className="text-2xl font-bold text-[#0f2d52] mb-4">15. Contact Us</h2>
              <p className="text-gray-600 leading-relaxed mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="space-y-3">
                <a href="mailto:info@legacytriangle.com" className="flex items-center gap-3 text-[#0f2d52] hover:text-[#48d480] transition-colors">
                  <Mail className="w-5 h-5 text-[#48d480]" />
                  info@legacytriangle.com
                </a>
                <a href="tel:+19844644672" className="flex items-center gap-3 text-[#0f2d52] hover:text-[#48d480] transition-colors">
                  <Phone className="w-5 h-5 text-[#48d480]" />
                  (984) 464-4672
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
